import { Controller } from "@hotwired/stimulus"

let chartModulePromise = null

function loadChartModule() {
  if (!chartModulePromise) {
    chartModulePromise = import("chart.js/auto").then((mod) => mod.Chart || mod.default)
  }
  return chartModulePromise
}

export default class extends Controller {
  static targets = ["chart"]
  static values = {
    data: Array
  }

  connect() {
    if (!this.hasChartTarget || !this.dataValue) return

    this._chartInitId = (this._chartInitId || 0) + 1
    const initId = this._chartInitId

    loadChartModule()
      .then((Chart) => {
        if (!this.element.isConnected || initId !== this._chartInitId) return

        this.Chart = Chart
        this.destroyChart()
        const ctx = this.chartTarget.getContext("2d")
        this.chart = this.createChart(ctx)
      })
      .catch((error) => {
        console.error("Chart failed to initialize:", error)
      })
  }

  disconnect() {
    this._chartInitId = (this._chartInitId || 0) + 1
    this.destroyChart()
  }

  destroyChart() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
      return
    }

    if (this.hasChartTarget && this.Chart) {
      const existing = this.Chart.getChart(this.chartTarget)
      existing?.destroy()
    }
  }

  createChart(ctx) {
    throw new Error("createChart must be implemented by subclass")
  }

  getBaseChartOptions() {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 1000, easing: "easeInOutQuart" },
      onHover: (event, activeElements) => {
        this.chartTarget.style.cursor = activeElements.length > 0 ? "pointer" : "default"
      }
    }
  }

  getDefaultLayout(padding = 5) {
    return {
      padding: {
        top: 10,
        bottom: 10,
        left: padding,
        right: padding
      }
    }
  }

  createYAxisConfig(max, stepSize) {
    return {
      beginAtZero: true,
      ...(max && { max }),
      ticks: {
        ...(stepSize && { stepSize }),
        color: "rgba(107, 114, 128, 0.8)",
        font: { size: 12 },
        precision: 0
      },
      grid: {
        color: "rgba(229, 231, 235, 0.5)",
        lineWidth: 1,
        drawBorder: false
      }
    }
  }

  createXAxisConfig(labels) {
    const config = {
      ticks: {
        color: "rgba(107, 114, 128, 0.8)",
        font: { size: 11 }
      },
      grid: { display: false }
    }

    if (labels) {
      config.categoryPercentage = 0.6
      config.barPercentage = 0.8
      config.ticks.maxRotation = labels.length > 7 ? 45 : 0
      config.ticks.minRotation = labels.length > 7 ? 45 : 0
      config.ticks.autoSkip = false
      config.ticks.callback = (value, index) => labels[index] || ""
    }

    return config
  }

  extractDataValues() {
    return this.dataValue.map(item => item.count || 0)
  }

  _interpolate(text, params) {
    return text.replace(/%\{(\w+)\}/g, (_, key) => params[key] != null ? params[key] : "")
  }
}
